import { Box } from '@lifeomic/chroma-react/components/Box';
import { defineMessages, useIntl } from 'react-intl';
import { File } from '../types';
import { makeStyles } from '@lifeomic/chroma-react/styles';
import {
  TableModule,
  TableConfiguration,
} from '@lifeomic/chroma-react/components/TableModule';
import { useFetchFileContents } from '../useFetchFileContents';
import { useNotify } from '@lifeomic/phc-web-toolkit/src/hooks/useNotify';
import Papa from 'papaparse';
import React from 'react';

const messages = defineMessages({
  fetchCSVContentsErrorToast: {
    id: 'files.fileDetailsModal.text.fetchCSVContentsErrorToast',
    defaultMessage: 'An error occurred when fetching the file contents',
  },
  processCSVErrorToast: {
    id: 'files.fileDetailsModal.text.processCSVErrorToast',
    defaultMessage: 'An error occurred processing the contents',
  },
});

const useStyles = makeStyles(theme => ({
  root: {
    overflowX: 'scroll',
    maxHeight: `calc(100vh - ${theme.pxToRem(200)})`,
  },
}));
interface CSVViewProps {
  file?: File;
}

const compare = (left: any, right: any) =>
  left.localeCompare(right, undefined, { numeric: true });

export const CSVView: React.FC<CSVViewProps> = ({ file }) => {
  const classes = useStyles({});

  const { formatMessage } = useIntl();
  const [addNote] = useNotify();

  const [headerConfig, setHeaderConfig] = React.useState<
    Array<TableConfiguration>
  >([]);
  const [tableRows, setTableRows] = React.useState<Array<any>>([]);
  const [sort, setSort] = React.useState({ key: null, direction: null });

  // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
  const { fileContents, isLoading, hasError } = useFetchFileContents(file);

  let sortedData = tableRows;

  if (sort.key) {
    sortedData = tableRows.slice().sort((a, b) =>
      sort.direction === 'asc' || !sort.direction
        ? // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          compare(a[sort.key], b[sort.key])
        : // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          compare(b[sort.key], a[sort.key]),
    );
  }

  React.useEffect(() => {
    if (!fileContents) {
      return;
    }

    try {
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      const allCSVRows = fileContents.split(/\r\n|\n/);

      if (allCSVRows.length === 0) {
        return;
      }

      const headers: Array<TableConfiguration> = [];
      // First row is always the header
      // build the config for the TableModule component
      const firstRow = allCSVRows?.[0]?.split(/[,\t]/g);

      for (const heading of firstRow) {
        headers.push({
          header: {
            label: heading,
            onSort: ({ sortDirection }) =>
              // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
              setSort({ key: heading, direction: sortDirection }),
          },
          cell: {
            content: h => h[heading],
          },
        });
      }

      setHeaderConfig(headers);
      const parsed = Papa.parse(fileContents, {
        header: true,
        skipEmptyLines: true,
      });
      setTableRows(parsed.data);
    } catch (error) {
      console.error('Processing the CSV contents has failed.', error);
      addNote(formatMessage(messages.processCSVErrorToast), 'error');
    }
  }, [addNote, fileContents, formatMessage, setSort]);

  React.useEffect(() => {
    if (hasError) {
      addNote(formatMessage(messages.fetchCSVContentsErrorToast), 'error');
    }
  }, [addNote, formatMessage, hasError]);

  return (
    <Box className={classes.root} fullWidth>
      <TableModule
        isLoading={isLoading}
        config={headerConfig}
        data={sortedData}
      />
    </Box>
  );
};
